import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChatPanel } from "../components/homestay/ChatPanel";
import { Stars } from "../components/homestay/ReviewsPanel";
import { MyBookings } from "../components/homestay/MyBookings";
import { errMsg } from "../components/homestay/constants";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../components/homestay/constants";

const STATUS_COLOR = { pending: BRAND.orange, approved: BRAND.green, rejected: "#DC2626", open: "#DC2626", in_progress: BRAND.orange, resolved: BRAND.green };

function Section({ title, tid, empty, children }) {
  return (
    <div data-testid={tid} className="rounded-3xl border-2 bg-white p-5" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold mb-2" style={{ color: BRAND.green }}>{title}</div>
      {children.length ? children : <div className="text-xs" style={{ color: BRAND.muted }}>{empty}</div>}
    </div>
  );
}

const Row = ({ left, sub, status, tid }) => (
  <div data-testid={tid} className="py-2 border-t flex justify-between gap-2 text-sm" style={{ borderColor: BRAND.line }}>
    <div className="min-w-0"><b>{left}</b><div className="text-xs" style={{ color: BRAND.muted }}>{sub}</div></div>
    <span className="text-[10px] font-bold uppercase shrink-0" style={{ color: STATUS_COLOR[status] || BRAND.green }}>{status.replace("_", " ")}</span>
  </div>
);

function ReviewForm({ item, authFetch, API, onDone }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const submit = async () => {
    try { await authFetch({ method: "post", url: `${API}/homestay/public/${item.property.id}/reviews`, data: { rating, text: text || null } }); toast.success("Thanks for your review!"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not submit review")); }
  };
  return (
    <div data-testid={`review-form-${item.booking_id}`} className="py-3 border-t" style={{ borderColor: BRAND.line }}>
      <div className="font-bold text-sm">{item.property?.name} <span className="text-xs font-normal" style={{ color: BRAND.muted }}>{item.check_in}{item.check_out ? ` → ${item.check_out}` : ""}</span></div>
      <div className="mt-1"><Stars value={rating} size={24} onPick={setRating} tid={`review-stars-${item.booking_id}`} /></div>
      <textarea rows={2} data-testid={`review-text-${item.booking_id}`} className="mt-2 w-full rounded-xl border-2 px-3 py-2 text-sm" placeholder="How was your stay?" value={text} onChange={(e) => setText(e.target.value)} />
      <button disabled={!rating} data-testid={`review-submit-${item.booking_id}`} onClick={submit} className="mt-1 rounded-full px-5 py-2 text-sm font-bold text-white disabled:opacity-50" style={{ background: BRAND.orange }}>Submit review</button>
    </div>
  );
}

export default function MyStay() {
  const { user, loading, authFetch, API } = useAuth();
  const [d, setD] = useState(null);
  const [sp] = useSearchParams();
  const [reviewable, setReviewable] = useState([]);
  const loadReviewable = () => authFetch({ url: `${API}/homestay/my-reviewable` }).then((r) => setReviewable(r.data.items)).catch(() => {});
  useEffect(() => { if (user) loadReviewable(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) authFetch({ url: `${API}/homestay/my-stay` }).then((r) => setD(r.data)).catch(() => setD({ stays: [], requests: [], complaints: [] })); }, [user, authFetch, API]);
  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/homestay/my-stay" replace />;
  if (!d) return <div className="max-w-4xl mx-auto px-4 py-10 text-sm" style={{ color: BRAND.muted }}>Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-4" data-testid="my-stay-page">
      <h1 className="font-display text-4xl font-bold" style={{ color: BRAND.green }}>My Stay</h1>
      <MyBookings authFetch={authFetch} API={API} />
      {reviewable.length > 0 && (
        <Section title="Feedback for your stay" tid="my-reviewable" empty="">
          {reviewable.map((it) => <ReviewForm key={it.booking_id} item={it} authFetch={authFetch} API={API} onDone={loadReviewable} />)}
        </Section>
      )}
      <div data-testid="my-chats">
        <div className="text-sm font-bold mb-2" style={{ color: BRAND.green }}>Chat with your property</div>
        <ChatPanel threadsUrl={`${API}/homestay/my-chats`} me="guest" userId={user.id} authFetch={authFetch} API={API} initialThreadId={sp.get("chat")} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="My bed requests" tid="my-requests" empty="No requests yet.">
          {d.requests.map((r) => <Row key={r.id} tid={`my-request-${r.id}`} left={`${r.property_name} · ${r.kind === "bed_change" ? "Bed change" : "Booking"}`} sub={`${r.check_in}${r.assigned_bed_label ? ` · Bed ${r.assigned_bed_label}` : ""}${r.reason ? ` · ${r.reason}` : ""}`} status={r.status} />)}
        </Section>
        <Section title="My complaints & suggestions" tid="my-complaints" empty="Nothing raised.">
          {d.complaints.map((c) => <Row key={c.id} tid={`my-complaint-${c.id}`} left={`${c.property_name} · ${c.category}`} sub={c.response ? `Reply: ${c.response}` : c.text} status={c.status} />)}
        </Section>
      </div>
    </div>
  );
}
